import React, { useState, useRef, useEffect } from 'react';
import { trackGetStartedClick } from '../utils/analytics';
import ContactSalesModal from './ContactSalesModal';
import './Pricing.css';

const Pricing = () => {
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(1); // Start on Starter so Free plan peeks from left
  const [showTokenPackages, setShowTokenPackages] = useState(false);
  const scrollContainerRef = useRef(null);

  // Redirect to testmindfrontend (app.qate.ai) for signup
  const saasUrl = process.env.REACT_APP_SAAS_URL || 'https://app.qate.ai';

  // Scroll to a specific card
  const scrollToCard = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cards = container.querySelectorAll('.pricing-card');
      if (cards[index]) {
        const card = cards[index];
        const containerWidth = container.offsetWidth;
        const cardWidth = card.offsetWidth;
        const scrollLeft = card.offsetLeft - (containerWidth - cardWidth) / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  };

  // Handle scroll to update active card
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cards = container.querySelectorAll('.pricing-card');
      const containerCenter = container.scrollLeft + container.offsetWidth / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(containerCenter - cardCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveCardIndex(closestIndex);
    }
  };

  // Scroll to Starter plan on mobile mount
  useEffect(() => {
    if (window.innerWidth <= 768 && scrollContainerRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => scrollToCard(1), 100);
    }
  }, []);

  const handlePlanButtonClick = (e, planKey) => {
    e.preventDefault();
    if (planKey === 'enterprise') {
      setShowContactSalesModal(true);
    } else {
      trackGetStartedClick(`pricing-${planKey}`);
      window.gtag_report_conversion(`${saasUrl}/get-started?plan=${planKey}`);
    }
  };

  const plans = [
    {
      key: "free",
      name: "Free",
      price: "0",
      period: "forever",
      description: "Perfect for trying out our AI testing platform",
      appLimit: 3,
      features: [
        "Up to 3 applications",
        "Web application testing",
        "REST API testing",
        "SOAP service testing",
        "CI pipeline integration",
        "Private datasets",
        "200 credits/month included",
        "1,500 welcome credits to get started",
        "AI test failure analysis",
        "AI test healing suggestions",
        "Knowledge base buildup",
        "Jira integration",
        "GitHub and Bitbucket integration",
        "Community support"
      ],
      buttonText: "Get Started Free",
      popular: false
    },
    {
      key: "starter",
      name: "Starter",
      price: "49",
      currency: "€",
      period: "month",
      description: "Perfect for small teams exploring conversational AI testing",
      appLimit: 5,
      features: [
        "Up to 5 applications",
        "Web application testing",
        "REST API testing",
        "SOAP service testing",
        "Test execution via your CI/CD pipeline",
        "500 in-platform test steps/month",
        "Private datasets",
        "3,000 credits/month included",
        "AI test failure analysis",
        "AI test healing suggestions",
        "AI bug fix suggestions",
        "GitHub bug issue creation",
        "Copilot bug solving",
        "Knowledge base buildup",
        "Jira integration",
        "GitHub and Bitbucket integration",
        "Email support"
      ],
      buttonText: "Start with Starter",
      popular: false
    },
    {
      key: "professional",
      name: "Professional",
      price: "599",
      currency: "€",
      period: "month",
      description: "Complete AI testing platform with Windows desktop + web coverage",
      appLimit: 10,
      features: [
        "Up to 10 applications",
        "Web and desktop application testing",
        "REST API testing",
        "SOAP service testing",
        "CI pipeline integration",
        "Test scheduling",
        "3,000 in-platform test steps/month",
        "Private datasets",
        "20,000 credits/month included",
        "AI test failure analysis",
        "AI test healing suggestions",
        "AI bug fix suggestions",
        "GitHub bug issue creation",
        "Copilot bug solving",
        "Knowledge base buildup",
        "Jira integration",
        "GitHub and Bitbucket integration",
        "Priority support"
      ],
      buttonText: "Start with Professional",
      popular: true,
      joke: "Your QA team will finally stop mass forwarding you bug reports 🙏"
    },
    {
      key: "enterprise",
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "Enterprise-grade AI testing with custom deployment and training",
      appLimit: null,
      features: [
        "Unlimited applications",
        "Everything in Professional",
        "On-premise deployment",
        "Custom AI training & tuning",
        "Dedicated support manager",
        "Advanced security & compliance",
        "SSO integration",
        "Custom reporting & analytics"
      ],
      buttonText: "Contact Sales",
      popular: false
    }
  ];

  // Top-up packages, sold in-app when a plan's monthly allowance runs out.
  // Keep in sync with testmindbackend/src/config/creditPackages.js.
  const tokenPackages = [
    { key: "small", name: "Small", tokens: "2,000", price: "29", recommended: false },
    { key: "medium", name: "Medium", tokens: "5,000", price: "74", recommended: true },
    { key: "large", name: "Large", tokens: "11,000", price: "149", recommended: false }
  ];

  return (
    <section className="pricing" id="pricing">
      <div className="pricing-container">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Generous credit allowances included. No hidden fees.</p>
        </div>

        {/* Mobile dot indicators - top */}
        <div className="pricing-dots pricing-dots-top">
          {plans.map((plan, index) => (
            <button
              key={index}
              className={`pricing-dot ${index === activeCardIndex ? 'active' : ''}`}
              onClick={() => scrollToCard(index)}
              aria-label={`Go to ${plan.name} plan`}
            >
              {index === 0 && <span className="dot-label">FREE</span>}
            </button>
          ))}
        </div>

        <div
          className="pricing-grid"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {plans.map((plan, index) => (
            <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              
              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-price">
                  {plan.currency && <span className="euro-symbol">{plan.currency}</span>}
                  <span className="amount">{plan.price}</span>
                  {plan.period && <span className="period">/{plan.period}</span>}
                </div>
                <div className="token-limit">
                  {plan.key === 'free' && '200 credits/month + 1,500 welcome credits'}
                  {plan.key === 'starter' && '3,000 credits/month'}
                  {plan.key === 'professional' && '20,000 credits/month'}
                  {plan.key === 'enterprise' && 'Custom token allocation'}
                </div>
                <p className="plan-description">{plan.description}</p>
              </div>
              
              <ul className="features-list">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="feature-item">
                    <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              
              <div className="plan-footer">
                <button
                  className={`plan-button ${plan.popular ? 'primary' : 'secondary'}`}
                  onClick={(e) => handlePlanButtonClick(e, plan.key)}
                >
                  {plan.buttonText}
                </button>
                {plan.joke && <p className="plan-joke">{plan.joke}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile dot indicators */}
        <div className="pricing-dots">
          {plans.map((plan, index) => (
            <button
              key={index}
              className={`pricing-dot ${index === activeCardIndex ? 'active' : ''}`}
              onClick={() => scrollToCard(index)}
              aria-label={`Go to ${plan.name} plan`}
            >
              {index === 0 && <span className="dot-label">FREE</span>}
            </button>
          ))}
        </div>

        <div className="token-packages">
          <button
            type="button"
            className="token-packages-toggle"
            onClick={() => setShowTokenPackages(!showTokenPackages)}
            aria-expanded={showTokenPackages}
            aria-controls="token-packages-panel"
          >
            Need more credits?
            <svg className={`token-packages-chevron ${showTokenPackages ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>

          {showTokenPackages && (
            <div className="token-packages-panel" id="token-packages-panel">
              <p className="token-packages-intro">Top up any plan with a one-time credit package. Purchased credits never expire and are used only after your monthly allowance runs out.</p>
              <div className="token-packages-grid">
                {tokenPackages.map((pkg) => (
                  <div key={pkg.key} className={`token-package-card ${pkg.recommended ? 'recommended' : ''}`}>
                    {pkg.recommended && <span className="token-package-badge">Most Popular</span>}
                    <span className="token-package-name">{pkg.name}</span>
                    <span className="token-package-tokens">{pkg.tokens} credits</span>
                    <span className="token-package-price"><span className="euro-symbol">€</span>{pkg.price}</span>
                  </div>
                ))}
              </div>
              <p className="token-packages-note">Prices exclude VAT. Credit packages are purchased from your account, whenever you need them.</p>
            </div>
          )}
        </div>

        <div className="pricing-footer">
          <p className="pricing-note">
            Start with our free plan - no credit card required. Upgrade anytime for more features.
          </p>
          <div className="pricing-faq">
            <h4>Frequently Asked Questions</h4>
            <div className="faq-grid">
              <div className="faq-item">
                <h5>Can I change plans anytime?</h5>
                <p>Yeah, sure! Upgrade or downgrade whenever you like. Changes take effect immediately.</p>
              </div>
              <div className="faq-item">
                <h5>What's included in the free plan?</h5>
                <p>The free plan includes web testing, REST API testing, SOAP service testing, CI pipeline integration, 200 credits per month plus 1,500 one-time welcome credits to get started, AI test failure analysis, Jira integration, GitHub and Bitbucket integration, and community support - perfect for getting started.</p>
              </div>
              <div className="faq-item">
                <h5>What is a credit?</h5>
                <p>A credit is 10,000 AI tokens on our base model (Claude Haiku). Heavier models weigh more per token: Sonnet ×2, Opus ×5, Fable ×10. Every plan includes a monthly credit allowance, and you can top up with credit packages that never expire. You pick the model per account, so you decide how far your credits go.</p>
              </div>
              <div className="faq-item">
                <h5>What counts as an in-platform test step?</h5>
                <p>Every step of a test that runs on our infrastructure: in cloud containers and in Windows desktop runs through the Qate agent, whether you start them manually, on a schedule or from your CI pipeline. Web tests executed by the Qate CLI in your own pipeline are unlimited and don't count.</p>
              </div>
              <div className="faq-item">
                <h5>Do you offer volume discounts?</h5>
                <p>Yes, we offer custom pricing for teams with 10+ applications or 5+ users. Contact our sales team for details.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showContactSalesModal && (
        <ContactSalesModal
          onClose={() => setShowContactSalesModal(false)}
        />
      )}
    </section>
  );
};

export default Pricing;